// Request keys for writes the server replays (TD-077, finance consolidation S7): an invoice's payment, its status and a
// team invoice's payout, each sent with `requestId` as main's Mark paid and Pay are. One key per attempt, named by its
// scope (the invoice and what is asked of it), so two invoices or two statuses never share one. The attempt keeps its key
// while it may have been recorded (no answer, or a 5xx: keyAfterFailure), so a retry answers the first write and never
// writes twice; a refusal or a success ends it, and the next attempt takes a fresh key.
import { useCallback, useRef } from "react";
import { keyAfterFailure, newRequestKey, type RequestKey } from "../utils/requestKey";

export interface RequestKeys {
  /** The key to send for `scope`'s attempt, made on its first send. */
  keyFor: (scope: string) => string;
  /** The attempt answered: `error` undefined for a success. */
  settled: (scope: string, error?: unknown) => void;
}

export function useRequestKeys(): RequestKeys {
  const keys = useRef(new Map<string, RequestKey>());
  const keyFor = useCallback((scope: string): string => {
    const key = keys.current.get(scope) ?? { id: newRequestKey(), unsure: false };
    keys.current.set(scope, key);
    return key.id;
  }, []);
  const settled = useCallback((scope: string, error?: unknown) => {
    const key = keys.current.get(scope);
    if (!key) return;
    if (error === undefined) keys.current.delete(scope);
    else keys.current.set(scope, keyAfterFailure(key, error));
  }, []);
  return { keyFor, settled };
}
